"use client";

import React, { useState } from "react";
import { Bell, Check, MessageCircle } from "lucide-react";
import { useRouter } from "next/navigation";
import { ProzUser } from "@/interfaces/account";
import { useNotifications, Notification } from "@/hooks/useNotifications";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { Button } from "@/components/shadcn/button";
import { toast } from "sonner";
import { useAppDispatch } from "@/lib/store/hooks";
import { setChatSliceBits } from "@/lib/store/features/chat/chatSlice";

const NotificationDropdown = ({ user }: { user: ProzUser }) => {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, fetchNotifications } =
    useNotifications();
  const router = useRouter();
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);

  // Get notification icon - only chat notifications
  const getNotificationIcon = () => {
    return <MessageCircle className="w-4 h-4 text-blue-500" />;
  };

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      await markAsRead([notification._id]);
    }

    // Handle chat notifications - navigate to chat and set channel
    if (notification.type === "chat" && notification.channelId) {
      // Set the channelId in Redux store
      dispatch(setChatSliceBits({ bitToSet: "channelId", value: notification.channelId }));
      // Navigate to chat page
      router.push("/chat");
    } else if (notification.redirectUrl) {
      // Handle other types of notifications with redirectUrl
      router.push(notification.redirectUrl);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    if (unreadCount > 0) {
      await markAllAsRead();
    }
  };

  // Handle dropdown open/close
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Refresh notifications when dropdown opens
      fetchNotifications();
    }
  };

  // Show empty state if no real notifications exist
  const showEmptyState = notifications.length === 0 && !loading;

  const displayNotifications = notifications;
  const displayUnreadCount = unreadCount;

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <div className="relative cursor-pointer">
          <Bell className="size-7 text-gray-600 dark:text-gray-300" />
          {displayUnreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 px-1.5 py-0.5 rounded-full text-white text-xs font-medium">
              {displayUnreadCount > 99 ? "99+" : displayUnreadCount}
            </span>
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="w-80 max-w-80 rounded-lg"
        side={"bottom"}
        align="center"
        sideOffset={26}
      >
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-medium">Notifications</h3>
          {displayUnreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs text-primary hover:text-primary/80"
            >
              Mark all read
            </Button>
          )}
        </div>

        <div className="max-h-60 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
              <p className="text-sm text-muted-foreground mt-2">Loading notifications...</p>
            </div>
          ) : displayNotifications.length > 0 ? (
            <ul>
              {displayNotifications.map((notification: any) => (
                <li
                  key={notification._id || notification.id}
                  className={`px-4 py-3 hover:bg-muted transition-colors cursor-pointer border-b last:border-b-0 ${
                    !notification.isRead ? "bg-blue-50 dark:bg-blue-950/20" : ""
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">{getNotificationIcon()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p
                            className={`text-sm font-medium ${!notification.isRead ? "text-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-400"}`}
                          >
                            {notification.title}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {notification.message}
                          </p>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-1">
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                          {notification.isRead && <Check className="w-3 h-3 text-gray-400" />}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500">
                          {formatTimeAgo(notification.createdAt)}
                        </span>
                        {notification.redirectUrl && (
                          <span className="text-xs text-primary">Click to view →</span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-8 text-center">
              <Bell className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                You'll see important updates here
              </p>
            </div>
          )}
        </div>

        {displayNotifications.length > 0 && (
          <div className="px-4 py-2 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              className="text-sm text-primary hover:underline"
              onClick={() => {
                // TODO: Navigate to full notifications page
                toast.info("Full notifications page coming soon!");
              }}
            >
              View All Notifications
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationDropdown;
